import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { isEmpty, isEqual } from 'lodash'

import {
  Box,
  Typography,
  useTheme,
  ClickAwayListener,
  Grow,
  ListItemText,
  useMediaQuery,
} from '@mui/material'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Check from '@mui/icons-material/Check'
import { useHistory } from 'react-router-dom'
import { OptionType } from '../../../types'

import { MobileAutocompleteModal } from '../MobileAutocompleteModal'
import {
  FilterSelectorButton,
  FilterSelectorCardLabel,
  FilterSelectorContainer,
  FilterSelectorList,
  FilterSelectorListItem,
  FilterSelectorPaper,
  FilterSelectorPopper,
} from './StyledComponents'

const FilterSelectorComponent: FC<{
  optionsList: OptionType[] | undefined
  setValue: (value: string) => void
  currentValue: string
  filterLabel?: string
  doubleRows?: boolean
  name?: string
}> = ({ optionsList, setValue, currentValue, filterLabel, doubleRows, name }) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const anchorRef = useRef<HTMLButtonElement>(null)

  const [open, setOpen] = useState<boolean>(false)

  const history = useHistory()
  const selectedItem = useMemo(() => optionsList?.find(({ key }) => key === currentValue), [
    optionsList,
    currentValue,
  ])

  const handleChange = useCallback(
    (selectedValue: string | null) => () => {
      selectedValue && setValue(selectedValue)
      setOpen(false)
      // for resetting page pagination to 1 when account changed
      const currentState = history.location.state || {}
      history.replace({ ...history.location, state: { ...currentState, page: 1 } })
    },
    [history],
  )

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }
    setOpen(false)
  }

  return (
    <>
      <FilterSelectorContainer data-test="filter">
        <FilterSelectorButton
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          className={open ? 'open' : ''}
          data-test="autotest-filterBtn"
          disableRipple
        >
          <Box width="100%" position="relative">
            <Typography
              variant="subtitle2"
              position="absolute"
              top={0}
              left={theme.spacing(1.5)}
              sx={{
                color: '#999999',
              }}
            >
              {filterLabel || name}
            </Typography>
            <FilterSelectorCardLabel noWrap>
              {selectedItem?.label || optionsList?.[0].label || '-'}
            </FilterSelectorCardLabel>
          </Box>
          {open ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ color: '#999999' }} />}
        </FilterSelectorButton>

        <FilterSelectorPopper
          open={open && !isSmallScreen}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          placement="bottom-start"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: placement !== 'bottom-start' ? 'left bottom' : 'left top' }}
            >
              <FilterSelectorPaper>
                <ClickAwayListener onClickAway={handleClose}>
                  <FilterSelectorList id="menu-list-grow">
                    {!isEmpty(optionsList) &&
                      optionsList?.map((method) => {
                        const { key, label } = method
                        return (
                          <FilterSelectorListItem
                            key={key}
                            onClick={handleChange(key)}
                            data-test={`autotest-${key}`}
                          >
                            <ListItemText
                              sx={{
                                flexGrow: 17,
                                flex: '0 0 auto',
                                width: '92%',
                              }}
                            >
                              <Trans i18nKey={label as string} defaults={label as string} />
                            </ListItemText>
                            {selectedItem?.key === key && <Check fontSize={'small'} />}
                          </FilterSelectorListItem>
                        )
                      })}
                  </FilterSelectorList>
                </ClickAwayListener>
              </FilterSelectorPaper>
            </Grow>
          )}
        </FilterSelectorPopper>
      </FilterSelectorContainer>

      {optionsList && (
        <MobileAutocompleteModal
          open={isSmallScreen && open}
          onClose={() => setOpen(false)}
          onSelect={handleChange}
          textHeader={name}
          options={optionsList}
          currentValue={currentValue}
          doubleRows={doubleRows}
        />
      )}
    </>
  )
}

export const FilterSelector = React.memo(FilterSelectorComponent, isEqual)
